import React from 'react';
import { Helmet } from 'react-helmet';
import Footer from './Footer';
import Nav from './Nav';

function DPA() {
  return (
    <>
      <Nav />
      <Helmet>
        <title>Data Processing Addendum | Speak Your Menu</title>
        <meta name="description" content="Read our Data Processing Addendum outlining data protection obligations between Speak Your Menu and customers." />
        <link rel="canonical" href="https://speakyourmenu.com/dpa" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://speakyourmenu.com/" },
            { "@type": "ListItem", position: 2, name: "Data Processing Addendum", item: "https://speakyourmenu.com/dpa" },
          ],
        })}</script>
      </Helmet>
      <main id="main-content" tabIndex="-1" className="container my-5">
        <h1 className="mb-4">Speak Your Menu Data Processing Addendum (DPA)</h1>
        <p className="text-muted">Last Updated: July 15, 2025</p>

        {/* 1. Purpose & Scope */}
        <section>
          <h2>1. Purpose &amp; Scope</h2>
          <p>This DPA governs the Processor’s Processing of Personal Data on behalf of the Controller in connection with the Services described in the Agreement.</p>
        </section>

        {/* 2. Definitions */}
        <section>
          <h2>2. Definitions</h2>
          <ul className="ms-3">
            <li><strong>Personal Data</strong> – any information relating to an identified or identifiable natural person.</li>
            <li><strong>Process/Processing</strong> – any operation performed on Personal Data (collection, storage, use, disclosure, etc.).</li>
            <li><strong>Sub-processor</strong> – any third party engaged by the Processor to Process Personal Data.</li>
          </ul>
          <p>All capitalised terms not defined here have the meanings set out in the Agreement.</p>
        </section>

        {/* 3. Roles of the Parties */}
        <section>
          <h2>3. Roles of the Parties</h2>
          <p><strong>Controller</strong> is the data controller; <strong>Processor</strong> is the data processor with respect to Personal Data processed under the Agreement.</p>
        </section>

        {/* 4. Categories of Data & Data Subjects */}
        <section>
          <h2>4. Categories of Data &amp; Data Subjects</h2>
          <p><strong>Data Subjects:</strong> restaurant owners, managers, directors, and employees who use the Services.</p>
          <p><strong>Data Categories:</strong> name, business e-mail, role, lesson assignments, lesson progress and scores, badge metadata, restaurant identifiers, and log entries (UUID, action, timestamp, role, restaurant UUID).</p>
          <p><strong>Special Categories:</strong> none intentionally processed.</p>
        </section>

        {/* 5. Processor Obligations */}
        <section>
          <h2>5. Processor Obligations</h2>
          <ul className="ms-3">
            <li>Process Personal Data only on documented instructions from Controller;</li>
            <li>Ensure persons authorised to Process Personal Data are bound by confidentiality;</li>
            <li>Implement the technical and organisational security measures in § 8;</li>
            <li>Assist Controller in fulfilling data-subject rights requests;</li>
            <li>Notify Controller without undue delay of any Personal-Data Breach;</li>
            <li>Make available all information necessary to demonstrate compliance and allow reasonable audits (see § 10).</li>
          </ul>
        </section>

        {/* 6. Sub-processors */}
        <section>
          <h2>6. Sub-processors</h2>
          <p>Processor may engage the Sub-processors listed at <a href="https://speakyourmenu.com/subprocessors" target="_blank" rel="noopener noreferrer">https://speakyourmenu.com/subprocessors</a>. Processor will notify Controller at least 10 days before adding or replacing a Sub-processor, giving Controller the right to raise reasonable objections.</p>
        </section>

        {/* 7. Cross-border Transfers */}
        <section>
          <h2>7. Cross-border Transfers</h2>
          <p>Personal Data may be accessed from the United States and, on a limited basis, from other jurisdictions (e.g., an authorised engineer in Pakistan). Processor relies on Standard Contractual Clauses or equivalent safeguards for such transfers.</p>
        </section>

        {/* 8. Security Measures */}
        <section>
          <h2>8. Security Measures</h2>
          <ul className="ms-3">
            <li>TLS 1.2/1.3 encryption in transit &amp; AES-256 encryption at rest;</li>
            <li>Role-based access controls and multi-factor authentication for privileged accounts;</li>
            <li>Annual third-party penetration tests and continuous vulnerability scanning;</li>
            <li>Daily encrypted backups with 30-day rotation;</li>
            <li>Documented incident-response and breach-notification procedures.</li>
          </ul>
        </section>

        {/* 9. Data-Subject Rights Assistance */}
        <section>
          <h2>9. Data-Subject Rights Assistance</h2>
          <p>Taking into account the nature of Processing, Processor will assist Controller—via appropriate technical and organisational measures—in fulfilling requests for access, correction, deletion, restriction, portability, and objection.</p>
        </section>

        {/* 10. Audits */}
        <section>
          <h2>10. Audits</h2>
          <p>Controller may audit Processor’s compliance once per year (or more often if required by law or after a confirmed data-breach) upon 30 days’ notice. Audits may be satisfied by a current SOC 2 Type II report or equivalent, or by on-site review during normal business hours.</p>
        </section>

        {/* 11. Deletion or Return of Data */}
        <section>
          <h2>11. Deletion or Return of Data</h2>
          <p>Upon termination of the Agreement, Processor will, at Controller’s choice, delete or return all Personal Data in its possession, except to the extent retention is required by law or necessary for legitimate business records (which will be deleted within 30 days of cessation of the legal basis).</p>
        </section>

        {/* 12. Liability & Indemnification */}
        <section>
          <h2>12. Liability &amp; Indemnification</h2>
          <p>The limitations of liability in the Agreement apply equally to this DPA. Controller shall indemnify Processor against claims arising from Processing that follows Controller’s instructions.</p>
        </section>

        {/* 13. Governing Law */}
        <section>
          <h2>13. Governing Law</h2>
          <p>This DPA is governed by the same law and dispute-resolution provisions as the Agreement (Delaware law; binding arbitration).</p>
        </section>

        {/* 14. Term */}
        <section>
          <h2>14. Term</h2>
          <p>This DPA remains in effect as long as Processor Processes Personal Data on behalf of Controller.</p>
        </section>

        {/* Contact */}
        <section>
          <h2>Contact</h2>
          <address>
            <strong>Speak Your Menu, Inc.</strong><br />
            Attn: Privacy Officer<br />
            89 Vanderbilt Ave, Apt. 1<br />
            Brooklyn, NY 11205 USA<br />
            Email: <a href="mailto:privacy@speakyourmenu.com">privacy@speakyourmenu.com</a>
          </address>
        </section>
      </main>
      <Footer />
    </>
  );
}

export default DPA;
